import React, { useCallback, useEffect, useState } from 'react';
import { FileItemProps } from '../FileItem/FileItem.component';
import FolderBreadCrumbs from '../FolderBreadCrumbs/FolderBreadCrumbs.component';
import FolderViewHeader from '../FolderViewHeader/FolderViewHeader.component';
import { FileActionMenuItems, FileActionMenuItem } from './FileActionMenuItems';
import {
  getFolder,
  encodeFileName,
  decodeFileName,
} from '@Helper/FolderHelper';
import FileListView, {
  FileListProps,
} from '../FileListView/FileListView.component';
import SearchedFileList from '../SearchedFileList/SearchedFileList.component';
import SearchInput from '@components/UI/SearchInput/SearchInput';
import './FolderView.component.scss';
import { MergefloServices } from '@Helper/MergefloServices';
import { useDispatch } from 'react-redux';
import { postProjectTemplate } from '@Store/feature/personalTemplatesDetailSlice';

export type FolderViewProps = {
  files: FileItemProps[];
  homePath: string;
  listType?: string;
  targetPath?: string;
  actionMenuItems?: FileActionMenuItem[];
  onActionMenuItemClick?: (file: FileItemProps, action: string) => void;
  handlePathChange?: (paths: string[]) => void;
  handleFileSelected?: (selectedFiles: SelectedFile[]) => void;
};

export type SelectedFile = {
  path: string;
  file: FileItemProps;
};

export const FOLDER_SEPARATOR: string = '/';

const FolderView = ({
  files,
  homePath,
  listType = 'list',
  targetPath,
  actionMenuItems = FileActionMenuItems,
  onActionMenuItemClick,
  handlePathChange,
  handleFileSelected,
}: FolderViewProps) => {
  const [fileList, setFileList] = useState<FileItemProps[]>([]);
  const [folderMap, setFolderMap] = useState<string>(targetPath ?? homePath);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const mergefloService = new MergefloServices();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState<string>('');
  const handleOnPathClicked = (file: FileItemProps) => {
    setSearchText('');
    setFolderMap(`${folderMap}${FOLDER_SEPARATOR}${encodeFileName(file.name)}`);
  };

  useEffect(() => {
    goToFolder();
    const decodedFolderPath = decodeFileName(folderMap);
    const pathList = decodedFolderPath
      .slice(
        decodedFolderPath.indexOf(FOLDER_SEPARATOR) + 1,
        decodedFolderPath.length
      )
      .split(FOLDER_SEPARATOR);
    const inHomePath = pathList.join(FOLDER_SEPARATOR) === homePath;
    handlePathChange && handlePathChange(inHomePath ? [] : pathList);
  }, [folderMap]);

  useEffect(() => {
    setFileList(files);
    goToFolder();
  }, [files]);

  useEffect(() => {
    goToFolder();
  }, []);

  useEffect(() => {
    mergefloService.getTemplateStructure().then((template: any) => {
      console.log({ template });
      dispatch(
        postProjectTemplate(
          (template['personal_templates'] as FileItemProps[]) || []
        )
      );
    });
  }, []);

  useEffect(() => {
    targetPath && setFolderMap(targetPath);
  }, [targetPath]);

  const goToFolder = useCallback(() => {
    const decodedFolderPath = decodeFileName(folderMap);
    console.log('folderMap ', decodedFolderPath);
    const pathList = decodedFolderPath
      .slice(
        decodedFolderPath.indexOf(FOLDER_SEPARATOR) + 1,
        decodedFolderPath.length
      )
      .split(FOLDER_SEPARATOR);
    let sourcePath = JSON.parse(JSON.stringify(files));
    if (decodedFolderPath !== homePath) {
      sourcePath = getFolder(sourcePath as FileItemProps[], pathList);
    }

    setFileList(Array.isArray(sourcePath) ? sourcePath : sourcePath.files);
  }, [folderMap, files]);

  const onPathClicked = (folderName: string) => {
    setFolderMap(folderName);
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
  };

  const onChangeChecked = (selectedFile: FileItemProps, checked: boolean) => {
    let newSelectedFiles;
    if (checked) {
      newSelectedFiles = [
        ...selectedFiles,
        { path: folderMap, file: selectedFile },
      ];
    } else {
      newSelectedFiles = selectedFiles.filter(
        ({ file }) => file.id !== selectedFile.id
      );
    }

    setSelectedFiles(newSelectedFiles);
    handleFileSelected && handleFileSelected(newSelectedFiles);
  };

  const fileListProps: FileListProps = {
    handleOnPathClicked,
    actionMenuItems,
    onActionMenuItemClick,
    onChangeChecked,
  };

  return (
    <div className="folder-view">
      <div className="folder-view__head-bar">
        <FolderBreadCrumbs
          files={files}
          path={folderMap}
          onPathClicked={onPathClicked}
        />
        <SearchInput
          onChange={handleSearchChange}
          value={searchText}
          placeholder="Search templates & folders"
        />
      </div>
      {listType === 'list' && (
        <div className="folder-view__head">
          <FolderViewHeader />
        </div>
      )}
      <div className={`folder-view__content`}>
        <div className={`folder-view__content__${listType}`}>
          {searchText.length > 0 && (
            <SearchedFileList
              sourcePath={files}
              searchText={searchText}
              listType={listType}
              fileListProps={fileListProps}
            />
          )}
          {searchText.length === 0 && (
            <FileListView
              fileList={fileList}
              fileListProps={fileListProps}
              listType={listType}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FolderView;
